import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Alert,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { DateTime } from 'luxon';
import { useAuth } from '../auth/auth-provider';
import {
    IPlaylistAdminDto,
    IPlaylistImportResultDto,
    IStoredFileDto,
    UpsertPlaylistItemDto,
} from '../clients/services';
import { MainContent } from '../components/MainContent';
import { PlaylistAdminService } from '../services';
import { ImageDropZone } from './AdminShared/ImageDropZone';
import { MediaPickerDialog } from './AdminShared/MediaPickerDialog';

const playlistService = new PlaylistAdminService();

const monthOptions = [
    { value: 0, label: 'Jahresliste' },
    ...Array.from({ length: 12 }).map((_, index) => ({
        value: index + 1,
        label: DateTime.fromObject({ month: index + 1 }).toFormat('LLLL'),
    })),
];

const normalize = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

const clonePlaylist = (playlist: IPlaylistAdminDto): IPlaylistAdminDto => {
    const rows = (playlist.playlist ?? []).map(
        (item) =>
            new UpsertPlaylistItemDto({
                artist: item.artist ?? '',
                title: item.title ?? '',
            }),
    );

    const hasTrailingEmptyRow =
        rows.length > 0 &&
        (rows[rows.length - 1].artist ?? '').trim().length === 0 &&
        (rows[rows.length - 1].title ?? '').trim().length === 0;

    return {
        ...playlist,
        playlist: hasTrailingEmptyRow ? rows : [...rows, new UpsertPlaylistItemDto({ artist: '', title: '' })],
    };
};

const getPlaylistLabel = (playlist: IPlaylistAdminDto): string => {
    const numericYear = Number(playlist.year);
    const numericMonth = Number(playlist.month ?? 0);

    if (!Number.isFinite(numericYear) || numericYear <= 0) {
        return 'Unbekanntes Jahr';
    }

    if (numericMonth >= 1 && numericMonth <= 12) {
        return `${DateTime.fromObject({ year: numericYear, month: numericMonth }).toFormat('LLLL')} ${numericYear}`;
    }

    return `Best of year ${numericYear}`;
};

const filterSuggestions = (source: string[], query: string): string[] => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) {
        return source.slice(0, 80);
    }

    return source.filter((item) => normalize(item).includes(normalizedQuery)).slice(0, 80);
};

const artistKey = (value: string): string => normalize(value);

const normalizeAlbumsMap = (source: Map<string, string[]>): Map<string, string[]> => {
    const next = new Map<string, Set<string>>();

    source.forEach((albums, key) => {
        const normalizedKey = artistKey(key);
        if (!normalizedKey) {
            return;
        }

        if (!next.has(normalizedKey)) {
            next.set(normalizedKey, new Set<string>());
        }

        albums.forEach((album) => {
            const trimmed = album.trim();
            if (trimmed.length > 0) {
                next.get(normalizedKey)?.add(trimmed);
            }
        });
    });

    const output = new Map<string, string[]>();
    next.forEach((albums, key) => {
        output.set(
            key,
            Array.from(albums).sort((a, b) => a.localeCompare(b, 'de-CH')),
        );
    });

    return output;
};

const buildAllAlbumsFromMap = (source: Map<string, string[]>): string[] => {
    const unique = new Map<string, string>();

    source.forEach((albums) => {
        albums.forEach((album) => {
            const trimmed = album.trim();
            const key = normalize(trimmed);
            if (trimmed.length > 0 && !unique.has(key)) {
                unique.set(key, trimmed);
            }
        });
    });

    return Array.from(unique.values()).sort((a, b) => a.localeCompare(b, 'de-CH'));
};

const isEmptyPlaylistItem = (item: UpsertPlaylistItemDto): boolean => {
    return (item.artist ?? '').trim().length === 0 && (item.title ?? '').trim().length === 0;
};

const ensureSingleTrailingEmptyRow = (items: UpsertPlaylistItemDto[]): UpsertPlaylistItemDto[] => {
    const next = items.map(
        (item) =>
            new UpsertPlaylistItemDto({
                artist: item.artist ?? '',
                title: item.title ?? '',
            }),
    );

    while (
        next.length > 1 &&
        isEmptyPlaylistItem(next[next.length - 1]) &&
        isEmptyPlaylistItem(next[next.length - 2])
    ) {
        next.pop();
    }

    if (next.length === 0 || !isEmptyPlaylistItem(next[next.length - 1])) {
        next.push(new UpsertPlaylistItemDto({ artist: '', title: '' }));
    }

    return next;
};

export const PlaylistsAdmin: React.FC = () => {
    const { profile, hasRole } = useAuth();
    const isAllowed = hasRole(profile, 'admin');

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const [playlists, setPlaylists] = useState<IPlaylistAdminDto[]>([]);
    const [selectedPlaylistId, setSelectedPlaylistId] = useState<number | undefined>();
    const [draft, setDraft] = useState<IPlaylistAdminDto | null>(null);

    const [newYear, setNewYear] = useState<number>(DateTime.now().year);
    const [newMonth, setNewMonth] = useState<number>(DateTime.now().month);

    const [artistSuggestions, setArtistSuggestions] = useState<string[]>([]);
    const [albumsByArtist, setAlbumsByArtist] = useState<Map<string, string[]>>(new Map());
    const [allAlbums, setAllAlbums] = useState<string[]>([]);
    const [newArtistName, setNewArtistName] = useState('');
    const [catalogArtist, setCatalogArtist] = useState('');
    const [newAlbumTitle, setNewAlbumTitle] = useState('');
    const [artistQuery, setArtistQuery] = useState('');
    const [editingArtistName, setEditingArtistName] = useState<string | null>(null);
    const [editingArtistValue, setEditingArtistValue] = useState('');
    const [editingAlbumTitle, setEditingAlbumTitle] = useState<string | null>(null);
    const [editingAlbumValue, setEditingAlbumValue] = useState('');

    const [mediaFiles, setMediaFiles] = useState<IStoredFileDto[]>([]);
    const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

    const [importFile, setImportFile] = useState<File | null>(null);
    const [importPreview, setImportPreview] = useState<IPlaylistImportResultDto | null>(null);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    const selectedPlaylist = useMemo(
        () => playlists.find((playlist) => playlist.id === selectedPlaylistId),
        [playlists, selectedPlaylistId],
    );

    const artistTableRows = useMemo(() => {
        return artistSuggestions.map((name) => ({ name }));
    }, [artistSuggestions]);

    const filteredArtistRows = useMemo(() => {
        const normalizedQuery = normalize(artistQuery);
        if (!normalizedQuery) {
            return artistTableRows;
        }

        return artistTableRows.filter((row) => normalize(row.name).includes(normalizedQuery));
    }, [artistTableRows, artistQuery]);

    const albumsForCatalogArtist = useMemo(() => {
        if (!catalogArtist) {
            return [];
        }

        return albumsByArtist.get(artistKey(catalogArtist)) ?? [];
    }, [albumsByArtist, catalogArtist]);

    const playlistsWithDraft = useMemo(() => {
        if (!draft || selectedPlaylistId === undefined) {
            return playlists;
        }

        return playlists.map((playlist) => {
            if (playlist.id !== selectedPlaylistId) {
                return playlist;
            }

            return {
                ...playlist,
                playlist: (draft.playlist ?? []).map(
                    (item) =>
                        new UpsertPlaylistItemDto({
                            artist: item.artist ?? '',
                            title: item.title ?? '',
                        }),
                ),
            };
        });
    }, [playlists, draft, selectedPlaylistId]);

    const usedArtists = useMemo(() => {
        const set = new Set<string>();

        playlistsWithDraft.forEach((playlist) => {
            (playlist.playlist ?? []).forEach((item) => {
                const key = artistKey(item.artist ?? '');
                if (key) {
                    set.add(key);
                }
            });
        });

        return set;
    }, [playlistsWithDraft]);

    const usedArtistAlbumPairs = useMemo(() => {
        const set = new Set<string>();

        playlistsWithDraft.forEach((playlist) => {
            (playlist.playlist ?? []).forEach((item) => {
                const artist = artistKey(item.artist ?? '');
                const album = normalize(item.title ?? '');
                if (artist && album) {
                    set.add(`${artist}::${album}`);
                }
            });
        });

        return set;
    }, [playlistsWithDraft]);

    const updateSuggestionsState = (artists: string[], albumsMap: Map<string, string[]>, albums: string[]) => {
        setArtistSuggestions(artists);
        setAlbumsByArtist(normalizeAlbumsMap(albumsMap));
        setAllAlbums(albums);
    };

    const ensureArtist = (name: string): string => {
        const trimmed = name.trim();
        if (!trimmed) {
            return '';
        }

        const existing = artistSuggestions.find((item) => normalize(item) === normalize(trimmed));
        if (existing) {
            return existing;
        }

        setArtistSuggestions((current) => [...current, trimmed].sort((a, b) => a.localeCompare(b, 'de-CH')));
        return trimmed;
    };

    const ensureAlbum = (artistName: string, albumTitle: string): string => {
        const artist = artistName.trim();
        const album = albumTitle.trim();

        if (!artist || !album) {
            return '';
        }

        const canonicalArtist = ensureArtist(artist);
        const key = artistKey(canonicalArtist);

        setAlbumsByArtist((current) => {
            const next = new Map(current);
            const existing = next.get(key) ?? [];
            const hasAlbum = existing.some((item) => normalize(item) === normalize(album));

            if (!hasAlbum) {
                next.set(
                    key,
                    [...existing, album].sort((a, b) => a.localeCompare(b, 'de-CH')),
                );
            }

            return next;
        });

        setAllAlbums((current) => {
            if (current.some((item) => normalize(item) === normalize(album))) {
                return current;
            }

            return [...current, album].sort((a, b) => a.localeCompare(b, 'de-CH'));
        });

        return album;
    };

    const startRenameArtist = (artistName: string) => {
        setEditingArtistName(artistName);
        setEditingArtistValue(artistName);
    };

    const cancelRenameArtist = () => {
        setEditingArtistName(null);
        setEditingArtistValue('');
    };

    const saveRenameArtist = () => {
        if (!editingArtistName) {
            return;
        }

        const oldName = editingArtistName;
        const nextName = editingArtistValue.trim();
        if (!nextName) {
            setError('Bitte einen Künstlernamen eingeben.');
            return;
        }

        if (normalize(oldName) === normalize(nextName)) {
            cancelRenameArtist();
            return;
        }

        const duplicate = artistSuggestions.some(
            (name) => normalize(name) === normalize(nextName) && normalize(name) !== normalize(oldName),
        );
        if (duplicate) {
            setError('Dieser Künstlername existiert bereits.');
            return;
        }

        setArtistSuggestions((current) =>
            current
                .map((name) => (normalize(name) === normalize(oldName) ? nextName : name))
                .sort((a, b) => a.localeCompare(b, 'de-CH')),
        );

        setAlbumsByArtist((current) => {
            const next = new Map(current);
            const oldKey = artistKey(oldName);
            const newKey = artistKey(nextName);
            const oldAlbums = next.get(oldKey) ?? [];
            const existingAlbums = next.get(newKey) ?? [];

            next.delete(oldKey);
            next.set(
                newKey,
                Array.from(new Set([...existingAlbums, ...oldAlbums])).sort((a, b) => a.localeCompare(b, 'de-CH')),
            );

            return next;
        });

        if (normalize(catalogArtist) === normalize(oldName)) {
            setCatalogArtist(nextName);
        }

        if (draft) {
            setDraft({
                ...draft,
                playlist: (draft.playlist ?? []).map(
                    (item) =>
                        new UpsertPlaylistItemDto({
                            artist: normalize(item.artist ?? '') === normalize(oldName) ? nextName : item.artist ?? '',
                            title: item.title ?? '',
                        }),
                ),
            });
        }

        cancelRenameArtist();
        setSuccess('Künstler umbenannt.');
    };

    const deleteArtistFromCatalog = (artistName: string) => {
        const key = artistKey(artistName);
        if (usedArtists.has(key)) {
            setError('Künstler kann nicht gelöscht werden, da er bereits in einer Playlist verwendet wird.');
            return;
        }

        setArtistSuggestions((current) => current.filter((name) => normalize(name) !== normalize(artistName)));
        setAlbumsByArtist((current) => {
            const next = new Map(current);
            next.delete(key);
            setAllAlbums(buildAllAlbumsFromMap(next));
            return next;
        });

        if (normalize(catalogArtist) === normalize(artistName)) {
            setCatalogArtist('');
        }

        if (editingArtistName && normalize(editingArtistName) === normalize(artistName)) {
            cancelRenameArtist();
        }

        setSuccess('Künstler gelöscht.');
    };

    const startRenameAlbum = (albumTitle: string) => {
        setEditingAlbumTitle(albumTitle);
        setEditingAlbumValue(albumTitle);
    };

    const cancelRenameAlbum = () => {
        setEditingAlbumTitle(null);
        setEditingAlbumValue('');
    };

    const saveRenameAlbum = () => {
        const artist = catalogArtist.trim();
        if (!artist || !editingAlbumTitle) {
            return;
        }

        const oldTitle = editingAlbumTitle;
        const nextTitle = editingAlbumValue.trim();
        if (!nextTitle) {
            setError('Bitte einen Albumtitel eingeben.');
            return;
        }

        if (normalize(oldTitle) === normalize(nextTitle)) {
            cancelRenameAlbum();
            return;
        }

        const key = artistKey(artist);
        const existing = albumsByArtist.get(key) ?? [];
        const duplicate = existing.some(
            (title) => normalize(title) === normalize(nextTitle) && normalize(title) !== normalize(oldTitle),
        );
        if (duplicate) {
            setError('Dieser Albumtitel existiert bereits für den Künstler.');
            return;
        }

        setAlbumsByArtist((current) => {
            const next = new Map(current);
            const albums = next.get(key) ?? [];
            next.set(
                key,
                albums
                    .map((title) => (normalize(title) === normalize(oldTitle) ? nextTitle : title))
                    .sort((a, b) => a.localeCompare(b, 'de-CH')),
            );
            setAllAlbums(buildAllAlbumsFromMap(next));
            return next;
        });

        if (draft) {
            setDraft({
                ...draft,
                playlist: (draft.playlist ?? []).map(
                    (item) =>
                        new UpsertPlaylistItemDto({
                            artist: item.artist ?? '',
                            title:
                                artistKey(item.artist ?? '') === key &&
                                normalize(item.title ?? '') === normalize(oldTitle)
                                    ? nextTitle
                                    : item.title ?? '',
                        }),
                ),
            });
        }

        cancelRenameAlbum();
        setSuccess('Album umbenannt.');
    };

    const deleteAlbumFromCatalog = (artistName: string, albumTitle: string) => {
        const pairKey = `${artistKey(artistName)}::${normalize(albumTitle)}`;
        if (usedArtistAlbumPairs.has(pairKey)) {
            setError('Album kann nicht gelöscht werden, da es bereits in einer Playlist verwendet wird.');
            return;
        }

        const key = artistKey(artistName);
        setAlbumsByArtist((current) => {
            const next = new Map(current);
            const remaining = (next.get(key) ?? []).filter((title) => normalize(title) !== normalize(albumTitle));
            next.set(key, remaining);
            setAllAlbums(buildAllAlbumsFromMap(next));
            return next;
        });

        if (editingAlbumTitle && normalize(editingAlbumTitle) === normalize(albumTitle)) {
            cancelRenameAlbum();
        }

        setSuccess('Album gelöscht.');
    };

    const loadData = async (preferredPlaylistId?: number) => {
        setIsLoading(true);
        setError('');

        try {
            const loadedPlaylists = await playlistService.loadAdminPlaylistsAsync();
            const suggestions = await playlistService.loadSuggestionDataAsync(loadedPlaylists);

            setPlaylists(loadedPlaylists);
            updateSuggestionsState(suggestions.artists, suggestions.albumsByArtist, suggestions.albums);

            if (loadedPlaylists.length > 0) {
                const candidate =
                    loadedPlaylists.find((playlist) => playlist.id === preferredPlaylistId) ??
                    loadedPlaylists.find((playlist) => playlist.id === selectedPlaylistId) ??
                    loadedPlaylists[0];
                setSelectedPlaylistId(candidate.id);
                setDraft(clonePlaylist(candidate));
            } else {
                setSelectedPlaylistId(undefined);
                setDraft(null);
            }
        } catch {
            setError('Playlists konnten nicht geladen werden.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAllowed) {
            return;
        }

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllowed]);

    const selectPlaylist = (id: number) => {
        const playlist = playlists.find((item) => item.id === id);
        if (!playlist) {
            return;
        }

        setSelectedPlaylistId(id);
        setDraft(clonePlaylist(playlist));
    };

    const updateDraftField = <K extends keyof IPlaylistAdminDto>(field: K, value: IPlaylistAdminDto[K]) => {
        if (!draft) {
            return;
        }

        setDraft({
            ...draft,
            [field]: value,
        });
    };

    const updateItem = (index: number, patch: Partial<UpsertPlaylistItemDto>) => {
        if (!draft) {
            return;
        }

        const next = [...(draft.playlist ?? [])];
        const existing = next[index] ?? new UpsertPlaylistItemDto({ artist: '', title: '' });
        next[index] = new UpsertPlaylistItemDto({
            artist: patch.artist ?? existing.artist ?? '',
            title: patch.title ?? existing.title ?? '',
        });

        setDraft({ ...draft, playlist: ensureSingleTrailingEmptyRow(next) });
    };

    const removeItem = (index: number) => {
        if (!draft) {
            return;
        }

        const next = [...(draft.playlist ?? [])];
        next.splice(index, 1);
        setDraft({ ...draft, playlist: ensureSingleTrailingEmptyRow(next) });
    };

    const createPlaylist = async () => {
        setIsSaving(true);
        setError('');

        try {
            const created = await playlistService.createPlaylistAsync(newYear, newMonth > 0 ? newMonth : undefined);
            await loadData(created.id);
            setSuccess('Playlist erstellt.');
        } catch (error: unknown) {
            const status = Number((error as { status?: number } | undefined)?.status);
            if (status === 409) {
                setError('Für dieses Jahr und diesen Monat existiert bereits eine Playlist.');
            } else {
                setError('Playlist konnte nicht erstellt werden.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const savePlaylist = async () => {
        if (!draft) {
            return;
        }

        const cleanedItems = (draft.playlist ?? [])
            .map(
                (item) =>
                    new UpsertPlaylistItemDto({
                        artist: (item.artist ?? '').trim(),
                        title: (item.title ?? '').trim(),
                    }),
            )
            .filter((item) => item.artist.length > 0 || item.title.length > 0);

        if (cleanedItems.length === 0) {
            setError('Bitte mindestens einen Künstler oder ein Album eintragen.');
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            const saved = await playlistService.updatePlaylistAsync({
                ...draft,
                playlist: cleanedItems,
            });

            const nextPlaylists = playlists.map((item) => (item.id === saved.id ? saved : item));
            setPlaylists(nextPlaylists);
            setDraft(clonePlaylist(saved));
            setSuccess('Playlist gespeichert.');

            const refreshedSuggestions = await playlistService.loadSuggestionDataAsync(nextPlaylists);
            updateSuggestionsState(
                refreshedSuggestions.artists,
                refreshedSuggestions.albumsByArtist,
                refreshedSuggestions.albums,
            );
        } catch {
            setError('Playlist konnte nicht gespeichert werden.');
        } finally {
            setIsSaving(false);
        }
    };

    const deleteSelectedPlaylist = async () => {
        if (!selectedPlaylist) {
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            await playlistService.deletePlaylistAsync(selectedPlaylist.id);
            const remaining = playlists.filter((item) => item.id !== selectedPlaylist.id);
            setPlaylists(remaining);

            const refreshedSuggestions = await playlistService.loadSuggestionDataAsync(remaining);
            updateSuggestionsState(
                refreshedSuggestions.artists,
                refreshedSuggestions.albumsByArtist,
                refreshedSuggestions.albums,
            );

            if (remaining.length > 0) {
                setSelectedPlaylistId(remaining[0].id);
                setDraft(clonePlaylist(remaining[0]));
            } else {
                setSelectedPlaylistId(undefined);
                setDraft(null);
            }

            setSuccess('Playlist gelöscht.');
        } catch {
            setError('Playlist konnte nicht geloescht werden.');
        } finally {
            setIsSaving(false);
        }
    };

    const openMediaPicker = async () => {
        try {
            await refreshMediaFiles();
            setIsMediaPickerOpen(true);
        } catch {
            setError('Medien konnten nicht geladen werden.');
        }
    };

    const uploadMediaFromDialog = async (file: File): Promise<boolean> => {
        try {
            await playlistService.uploadImageAsync(file);
            return true;
        } catch {
            setError('Upload fehlgeschlagen.');
            return false;
        }
    };

    const refreshMediaFiles = async () => {
        try {
            const files = await playlistService.listMediaImagesAsync();
            setMediaFiles(files);
        } catch {
            setError('Medien konnten nicht geladen werden.');
        }
    };

    const setImage = (url: string) => {
        if (!draft) {
            return;
        }

        updateDraftField('image', url);
    };

    const executeDryRunImport = async () => {
        if (!importFile) {
            setError('Bitte zuerst eine Word-Datei auswählen.');
            return;
        }

        setIsImporting(true);
        setError('');

        try {
            const result = await playlistService.importPlaylistAsync(importFile, true);
            setImportPreview(result);
            setSuccess('Vorschau erstellt.');
        } catch {
            setError('Import-Vorschau fehlgeschlagen.');
        } finally {
            setIsImporting(false);
        }
    };

    const confirmImport = async () => {
        if (!importFile) {
            setError('Bitte zuerst eine Word-Datei auswählen.');
            return;
        }

        setIsImporting(true);
        setError('');

        try {
            const result = await playlistService.importPlaylistAsync(importFile, false);
            setImportPreview(result);
            await loadData(result.playlistId);
            setSuccess('Word-Datei importiert.');
        } catch {
            setError('Import fehlgeschlagen.');
        } finally {
            setIsImporting(false);
        }
    };

    if (!isAllowed) {
        return (
            <MainContent>
                <Alert severity="warning">Kein Zugriff.</Alert>
            </MainContent>
        );
    }

    if (isLoading) {
        return (
            <MainContent>
                <CircularProgress />
            </MainContent>
        );
    }

    return (
        <MainContent>
            <Stack
                direction="row"
                sx={{
                    height: 'calc(100vh - 220px)',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#ffffff',
                }}
            >
                <Box
                    sx={{
                        minWidth: 320,
                        maxWidth: 420,
                        width: '36%',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        p: 2,
                        overflow: 'auto',
                        backgroundColor: '#ffffff',
                    }}
                >
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Jahr"
                            value={newYear}
                            onChange={(event) => setNewYear(Number(event.target.value))}
                            sx={{ width: 180 }}
                        />
                        <TextField
                            size="small"
                            select
                            label="Monat"
                            value={newMonth}
                            onChange={(event) => setNewMonth(Number(event.target.value))}
                            fullWidth
                        >
                            {monthOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button variant="contained" disabled={isSaving} onClick={createPlaylist}>
                            Neu
                        </Button>
                    </Stack>

                    <List dense disablePadding>
                        {playlists.map((playlist) => (
                            <ListItemButton
                                key={playlist.id}
                                selected={selectedPlaylistId === playlist.id}
                                onClick={() => selectPlaylist(playlist.id)}
                                sx={{
                                    mb: 1,
                                    border: '1px solid',
                                    borderColor: selectedPlaylistId === playlist.id ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                }}
                            >
                                <ListItemText
                                    primary={getPlaylistLabel(playlist)}
                                    primaryTypographyProps={{ color: 'text.primary', fontWeight: 600 }}
                                />
                            </ListItemButton>
                        ))}
                        {playlists.length === 0 && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                                Keine Playlists vorhanden.
                            </Alert>
                        )}
                    </List>
                </Box>

                <Box sx={{ flex: 1, p: 2, overflow: 'auto', backgroundColor: '#ffffff' }}>
                    {!draft ? (
                        <Alert severity="info">Bitte links eine Playlist wählen oder neu erstellen.</Alert>
                    ) : (
                        <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                <TextField
                                    type="number"
                                    label="Jahr"
                                    value={draft.year}
                                    onChange={(event) => updateDraftField('year', Number(event.target.value))}
                                    sx={{ width: 230 }}
                                />
                                <TextField
                                    select
                                    label="Monat"
                                    value={draft.month ?? 0}
                                    onChange={(event) => {
                                        const value = Number(event.target.value);
                                        updateDraftField('month', value > 0 ? value : null);
                                    }}
                                    sx={{ width: 240 }}
                                >
                                    {monthOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>

                            <ImageDropZone
                                label="Playlist Bild"
                                imageUrl={draft.image ? playlistService.resolvePublicUrl(draft.image) : undefined}
                                imageTitle={getPlaylistLabel(draft)}
                                onFileDropped={async (file) => {
                                    const url = await playlistService.uploadImageAsync(file);
                                    setImage(url);
                                    setSuccess('Bild hochgeladen.');
                                }}
                                onOpenMediaPicker={openMediaPicker}
                                onClear={async () => {
                                    setImage('');
                                    setSuccess('Bild entfernt.');
                                }}
                                previewObjectFit="contain"
                                previewMaxHeight={260}
                            />

                            <Divider />

                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h6">Einträge</Typography>
                            </Stack>

                            <Stack spacing={1}>
                                {(draft.playlist ?? []).map((item, index) => {
                                    const artistValues = filterSuggestions(artistSuggestions, item.artist ?? '');
                                    const scopedAlbums = albumsByArtist.get(artistKey(item.artist ?? '')) ?? [];
                                    const albumSource = item.artist?.trim().length ? scopedAlbums : allAlbums;
                                    const albumValues = filterSuggestions(albumSource, item.title ?? '');

                                    return (
                                        <Box
                                            key={`entry-${index}`}
                                            onDragOver={(event) => event.preventDefault()}
                                            onDrop={() => {
                                                if (draggedIndex === null || draggedIndex === index || !draft) {
                                                    return;
                                                }

                                                const nextItems = [...(draft.playlist ?? [])];
                                                const moved = nextItems.splice(draggedIndex, 1)[0];
                                                nextItems.splice(index, 0, moved);
                                                setDraft({
                                                    ...draft,
                                                    playlist: ensureSingleTrailingEmptyRow(nextItems),
                                                });
                                                setDraggedIndex(null);
                                            }}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 1,
                                                p: 1,
                                                backgroundColor: '#ffffff',
                                            }}
                                        >
                                            <Stack
                                                direction={{ xs: 'column', md: 'row' }}
                                                spacing={1}
                                                alignItems="center"
                                            >
                                                <Typography
                                                    sx={{ width: 34, textAlign: 'center', color: 'text.secondary' }}
                                                >
                                                    {index + 1}
                                                </Typography>

                                                <Box
                                                    draggable
                                                    onDragStart={(event) => {
                                                        setDraggedIndex(index);
                                                        event.dataTransfer.effectAllowed = 'move';
                                                    }}
                                                    onDragEnd={() => setDraggedIndex(null)}
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'text.secondary',
                                                        cursor: 'grab',
                                                        px: 0.5,
                                                        userSelect: 'none',
                                                    }}
                                                    title="Zum Verschieben ziehen"
                                                >
                                                    <DragIndicatorIcon fontSize="small" />
                                                </Box>

                                                <Box sx={{ flex: 1, width: '100%' }}>
                                                    <Autocomplete
                                                        freeSolo
                                                        options={artistValues}
                                                        value={item.artist ?? ''}
                                                        onChange={(_, value) => {
                                                            const nextArtist = typeof value === 'string' ? value : '';
                                                            const canonicalArtist = ensureArtist(nextArtist);
                                                            updateItem(index, { artist: canonicalArtist });
                                                        }}
                                                        onInputChange={(_, value) => {
                                                            updateItem(index, { artist: value });
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                id={`playlist-entry-artist-${index}`}
                                                                fullWidth
                                                                size="small"
                                                                label="Künstler"
                                                                onBlur={() => {
                                                                    const typed = (item.artist ?? '').trim();
                                                                    if (!typed) {
                                                                        return;
                                                                    }

                                                                    const canonicalArtist = ensureArtist(typed);
                                                                    if (canonicalArtist !== item.artist) {
                                                                        updateItem(index, { artist: canonicalArtist });
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Box>

                                                <Box sx={{ flex: 1, width: '100%' }}>
                                                    <Autocomplete
                                                        freeSolo
                                                        options={albumValues}
                                                        value={item.title ?? ''}
                                                        onChange={(_, value) => {
                                                            const nextAlbum = typeof value === 'string' ? value : '';
                                                            const nextArtist = (item.artist ?? '').trim();
                                                            const canonicalAlbum =
                                                                ensureAlbum(nextArtist, nextAlbum) || nextAlbum;
                                                            updateItem(index, { title: canonicalAlbum });
                                                        }}
                                                        onInputChange={(_, value) => {
                                                            updateItem(index, { title: value });
                                                            const nextArtist = (item.artist ?? '').trim();
                                                            if (nextArtist && value.trim().length > 0) {
                                                                ensureAlbum(nextArtist, value);
                                                            }
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                id={`playlist-entry-album-${index}`}
                                                                fullWidth
                                                                size="small"
                                                                label="Album"
                                                                onBlur={() => {
                                                                    const typedAlbum = (item.title ?? '').trim();
                                                                    const typedArtist = (item.artist ?? '').trim();
                                                                    if (!typedArtist || !typedAlbum) {
                                                                        return;
                                                                    }

                                                                    const canonicalAlbum = ensureAlbum(
                                                                        typedArtist,
                                                                        typedAlbum,
                                                                    );
                                                                    if (canonicalAlbum !== item.title) {
                                                                        updateItem(index, { title: canonicalAlbum });
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Box>

                                                <Button color="error" onClick={() => removeItem(index)}>
                                                    Entfernen
                                                </Button>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Stack>

                            <Divider />

                            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                                <Box
                                    sx={{
                                        flex: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 1.5,
                                        backgroundColor: '#ffffff',
                                        color: '#1f1f1f',
                                        '& .MuiInputBase-input': {
                                            color: '#1f1f1f',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(31, 31, 31, 0.8)',
                                        },
                                    }}
                                >
                                    <Typography variant="h6" sx={{ mb: 1, color: '#1f1f1f' }}>
                                        Künstler Tabelle
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Künstler suchen"
                                        value={artistQuery}
                                        onChange={(event) => setArtistQuery(event.target.value)}
                                        sx={{ mb: 1 }}
                                    />
                                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Neuer Künstler"
                                            value={newArtistName}
                                            onChange={(event) => setNewArtistName(event.target.value)}
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                const canonical = ensureArtist(newArtistName);
                                                if (canonical) {
                                                    setCatalogArtist(canonical);
                                                    setNewArtistName('');
                                                }
                                            }}
                                        >
                                            Hinzufügen
                                        </Button>
                                    </Stack>
                                    <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
                                        {filteredArtistRows.map((row) => {
                                            const isEditing =
                                                editingArtistName !== null &&
                                                normalize(editingArtistName) === normalize(row.name);
                                            const artistInUse = usedArtists.has(artistKey(row.name));

                                            return (
                                                <ListItemButton
                                                    key={row.name}
                                                    selected={normalize(catalogArtist) === normalize(row.name)}
                                                    onClick={() => setCatalogArtist(row.name)}
                                                    sx={{ color: '#1f1f1f' }}
                                                >
                                                    {isEditing ? (
                                                        <Stack direction="row" spacing={0.5} sx={{ width: '100%' }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={editingArtistValue}
                                                                onChange={(event) =>
                                                                    setEditingArtistValue(event.target.value)
                                                                }
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    saveRenameArtist();
                                                                }}
                                                            >
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    cancelRenameArtist();
                                                                }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Stack>
                                                    ) : (
                                                        <Stack
                                                            direction="row"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            sx={{ width: '100%' }}
                                                        >
                                                            <ListItemText
                                                                primary={row.name}
                                                                primaryTypographyProps={{
                                                                    color: '#1f1f1f',
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                            <Stack direction="row" spacing={0.5}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        startRenameArtist(row.name);
                                                                    }}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    disabled={artistInUse}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        deleteArtistFromCatalog(row.name);
                                                                    }}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </Stack>
                                                    )}
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </Box>

                                <Box
                                    sx={{
                                        flex: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 1.5,
                                        backgroundColor: '#ffffff',
                                        color: '#1f1f1f',
                                        '& .MuiInputBase-input': {
                                            color: '#1f1f1f',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(31, 31, 31, 0.8)',
                                        },
                                    }}
                                >
                                    <Typography variant="h6" sx={{ mb: 1, color: '#1f1f1f' }}>
                                        Album Tabelle
                                    </Typography>

                                    <Stack spacing={1} sx={{ mb: 1 }}>
                                        <Autocomplete
                                            freeSolo
                                            options={artistSuggestions}
                                            value={catalogArtist}
                                            onChange={(_, value) => {
                                                const nextArtist = typeof value === 'string' ? value : '';
                                                const canonical = ensureArtist(nextArtist);
                                                setCatalogArtist(canonical);
                                            }}
                                            onInputChange={(_, value) => setCatalogArtist(value)}
                                            renderInput={(params) => (
                                                <TextField {...params} size="small" label="Künstler für Albumtabelle" />
                                            )}
                                        />

                                        <Stack direction="row" spacing={1}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Neues Album"
                                                value={newAlbumTitle}
                                                onChange={(event) => setNewAlbumTitle(event.target.value)}
                                            />
                                            <Button
                                                variant="outlined"
                                                disabled={!catalogArtist.trim()}
                                                onClick={() => {
                                                    const canonical = ensureAlbum(catalogArtist, newAlbumTitle);
                                                    if (canonical) {
                                                        setNewAlbumTitle('');
                                                    }
                                                }}
                                            >
                                                Hinzufügen
                                            </Button>
                                        </Stack>
                                    </Stack>

                                    <List dense disablePadding sx={{ maxHeight: 220, overflow: 'auto' }}>
                                        {albumsForCatalogArtist.map((album) => {
                                            const isEditing =
                                                editingAlbumTitle !== null &&
                                                normalize(editingAlbumTitle) === normalize(album);
                                            const albumInUse = usedArtistAlbumPairs.has(
                                                `${artistKey(catalogArtist)}::${normalize(album)}`,
                                            );

                                            return (
                                                <ListItemButton key={album} sx={{ color: '#1f1f1f' }}>
                                                    {isEditing ? (
                                                        <Stack direction="row" spacing={0.5} sx={{ width: '100%' }}>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                value={editingAlbumValue}
                                                                onChange={(event) =>
                                                                    setEditingAlbumValue(event.target.value)
                                                                }
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    saveRenameAlbum();
                                                                }}
                                                            >
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    cancelRenameAlbum();
                                                                }}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Stack>
                                                    ) : (
                                                        <Stack
                                                            direction="row"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                            sx={{ width: '100%' }}
                                                        >
                                                            <ListItemText
                                                                primary={album}
                                                                secondary={catalogArtist || 'Künstler'}
                                                                primaryTypographyProps={{
                                                                    color: '#1f1f1f',
                                                                    fontWeight: 600,
                                                                }}
                                                                secondaryTypographyProps={{
                                                                    color: 'rgba(31, 31, 31, 0.8)',
                                                                }}
                                                            />
                                                            <Stack direction="row" spacing={0.5}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        startRenameAlbum(album);
                                                                    }}
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    disabled={albumInUse}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        deleteAlbumFromCatalog(catalogArtist, album);
                                                                    }}
                                                                >
                                                                    <DeleteOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        </Stack>
                                                    )}
                                                </ListItemButton>
                                            );
                                        })}
                                    </List>
                                </Box>
                            </Stack>

                            <Divider />

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                <Button variant="contained" disabled={isSaving} onClick={savePlaylist}>
                                    Speichern
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    disabled={isSaving}
                                    onClick={deleteSelectedPlaylist}
                                >
                                    Playlist löschen
                                </Button>
                            </Stack>

                            <Divider />

                            <Stack spacing={1.2}>
                                <Typography variant="h6">Word Import</Typography>
                                <Button variant="outlined" component="label" disabled={isImporting}>
                                    Word-Datei wählen
                                    <input
                                        hidden
                                        type="file"
                                        accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={(event) => {
                                            const file = event.target.files?.[0] ?? null;
                                            setImportFile(file);
                                            setImportPreview(null);
                                        }}
                                    />
                                </Button>

                                {importFile && <Typography>{importFile.name}</Typography>}

                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                                    <Button
                                        variant="contained"
                                        disabled={!importFile || isImporting}
                                        onClick={executeDryRunImport}
                                    >
                                        Vorschau Laden
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="success"
                                        disabled={!importFile || !importPreview || isImporting}
                                        onClick={confirmImport}
                                    >
                                        Import Bestätigen
                                    </Button>
                                </Stack>

                                {importPreview && (
                                    <Alert severity="info">
                                        Jahr: {importPreview.year} | Monat: {importPreview.month ?? '-'} | Einträge:{' '}
                                        {importPreview.entries} | Trockenlauf: {importPreview.dryRun ? 'Ja' : 'Nein'}
                                    </Alert>
                                )}
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Stack>

            <MediaPickerDialog
                open={isMediaPickerOpen}
                files={mediaFiles}
                onClose={() => setIsMediaPickerOpen(false)}
                onUpload={uploadMediaFromDialog}
                onRefresh={refreshMediaFiles}
                onPick={(file) => {
                    const url = playlistService.resolvePublicUrl(file.publicUrl ?? '');
                    setImage(url);
                }}
            />

            <Snackbar open={error.length > 0} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert severity="error" onClose={() => setError('')}>
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar open={success.length > 0} autoHideDuration={2500} onClose={() => setSuccess('')}>
                <Alert severity="success" onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            </Snackbar>
        </MainContent>
    );
};

export default PlaylistsAdmin;
